import { TokenProvider } from '~/hooks/auth/useMsalTokenProvider';
import { handleError } from '~/utils/errors';
import {
    APIType,
    ContouringCustomizationFile,
    DoseCustomizationFile,
    EmptyLabeling,
    Labeling,
    PollData,
    PollingStatus,
    RegistrationCustomizationFile,
    TranslationCustomizationFile,
    UserCustomization,
    UserModels,
} from './types';

import AppConfig from '~/utils/appconfig';

/**
 * Parameters for the request to upload and process DICOM files. This objects is passed as a header to the backend
 * for each request and needs to be filled according to the API specification.
 */
export type RequestParameters = {
    action: string;
    nExpectedFiles?: string;
    taskUID?: string;
    targets?: string; // for dose prediction
    rois?: string; // for segmentation
    sessionId?: string; // if needs to be overriden
    fixedSeriesUID?: string;
    movingSeriesUID?: string;
};

export type BlobUploadType = { file: Blob; filename?: string };

// Define a type for the JSON returned from access-rights as a dictionary
interface AccessRightsType {
    [key: string]: string[];
}

export class BackendClient {
    public readonly sessionID: string;
    public readonly api: APIType;
    private baseURL: string;
    private tokenProvider: TokenProvider;
    protected abortController: AbortController = new AbortController();
    private readonly _accessRightKey: string = AppConfig.accessRightKey;

    constructor(
        baseURL: string,
        tokenProvider: TokenProvider,
        sessionID: string,
        api: APIType = APIType.CONTOURING
    ) {
        this.sessionID = sessionID;
        this.baseURL = baseURL.endsWith('/') ? baseURL : `${baseURL}/`;
        this.tokenProvider = tokenProvider;
        this.api = api;
    }

    authorizationHeader = async () => {
        const token = await this.tokenProvider.getToken();
        // don't add empty token
        return token?.token
            ? {
                  Authorization: token.token,
              }
            : Promise.resolve({});
    };

    defaultHeaders = async () => {
        return {
            ...(await this.authorizationHeader()),
            'Cache-Control': 'no-store',
        };
    };

    public async models(): Promise<UserModels> {
        const response = await fetch(
            this.envelopeURL(`${this.api}/list-active-models`),
            {
                headers: await this.defaultHeaders(),
                signal: this.abortController.signal,
            }
        );

        if (response.ok) {
            return response.json();
        } else {
            return {
                all_model_names: [],
                user_model_names: [],
                online_model_names: [],
                all_model_details: [],
                user_model_details: [],
            };
        }
    }

    public async accessRights(): Promise<boolean> {
        try {
            const response = await fetch(this.envelopeURL('access-rights'), {
                headers: await this.defaultHeaders(),
                signal: this.abortController.signal,
            });
            if (!response.ok) {
                // Throw server errors so React Query can retry
                throw new Error(`Server error: ${response.status}`);
            }
            const accessRights: AccessRightsType = await response.json();
            // Check if the specified key exists and includes 'access'
            if (
                accessRights[this._accessRightKey] &&
                accessRights[this._accessRightKey].includes('access')
            ) {
                return true;
            }
        } catch (error) {
            console.error(
                'An error occurred while fetching access rights:',
                error
            );
            throw error;
        }
        return false;
    }

    public async labeling(apiType: APIType): Promise<Labeling> {
        const urlPath =
            apiType === APIType.WORKSPACE ? 'labeling' : `${apiType}/labeling`;
        try {
            const response = await fetch(this.envelopeURL(urlPath), {
                headers: await this.defaultHeaders(),
                signal: this.abortController.signal,
            });
            if (!response.ok) {
                return EmptyLabeling;
            }
            return response.json();
        } catch (error) {
            console.error('An error occurred while fetching labeling:', error);
        }
        return EmptyLabeling;
    }

    public async modelCustomizations(
        userId: string
    ): Promise<
        UserCustomization<
            | TranslationCustomizationFile
            | DoseCustomizationFile
            | RegistrationCustomizationFile
            | ContouringCustomizationFile
        >[]
    > {
        const response = await fetch(
            this.envelopeURL(`${APIType.CONFIG}/${this.customizationPath()}`),
            {
                headers: {
                    ...(await this.defaultHeaders()),
                    userId,
                },
                signal: this.abortController.signal,
            }
        );

        if (response.ok) {
            return response.json();
        } else {
            return [];
        }
    }

    public async submitFile(
        blob: BlobUploadType,
        request: RequestParameters
    ): Promise<boolean> {
        const data = new FormData();
        data.append('file', blob.file, blob.filename);

        const options = {
            headers: {
                ...(await this.defaultHeaders()),
                ...request,
                skip_dicom_validation: 'False',
            },
            method: 'POST',
            body: data,
            signal: this.abortController.signal,
        };

        const params: { [index: string]: string } = {};

        if ('sessionId' in request && request.sessionId) {
            params['sessionId'] = request.sessionId!;
        }

        const response = await fetch(
            this.envelopeURL(`${this.api}`, params),
            options
        );
        if (response.ok) {
            return true;
        } else {
            handleError(
                new Error('Unsuccessful response'),
                'Server returned an unsuccessful response.'
            );
        }

        return false;
    }

    public async poll(): Promise<PollData> {
        const response = await fetch(this.envelopeURL(`${this.api}/poll`), {
            method: 'POST',
            headers: await this.defaultHeaders(),
            signal: this.abortController.signal,
        });
        if (response.status === 304) {
            return { status: PollingStatus.NO_RESULT };
        } else if (response.status === 200) {
            const result = await response.json();
            return { status: PollingStatus.HAS_RESULT, result };
        }

        const error = await response.text();
        return { status: PollingStatus.POLLING_FAILED, error };
    }

    public async downloadFile(
        resultId: string,
        path: string
    ): Promise<Blob | undefined> {
        try {
            const response = await fetch(
                this.envelopeURL(`${this.api}/result`, { resultId }),
                {
                    headers: {
                        ...(await this.defaultHeaders()),
                        path,
                    },
                    signal: this.abortController.signal,
                    cache: 'no-store',
                }
            );

            if (response.ok) {
                return response.blob();
            } else {
                handleError(
                    new Error('Unsuccessful response'),
                    'Server returned an unsuccessful response.'
                );
            }
        } catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') {
                return undefined;
            }
            handleError(error, "Couldn't download files succesfully.");
        }
    }

    public async deleteResult(resultId: string): Promise<string | undefined> {
        const response = await fetch(
            this.envelopeURL(`${this.api}/result`, { resultId }),
            {
                method: 'DELETE',
                headers: await this.defaultHeaders(),
                cache: 'no-store',
            }
        );
        return response.text();
    }

    public abort() {
        this.abortController.abort();
        this.abortController = new AbortController();
    }

    public get signal(): AbortSignal {
        return this.abortController.signal;
    }

    public async getErrors(resultId: string): Promise<string | undefined> {
        const response = await fetch(
            this.envelopeURL(`${this.api}/error-message`, { resultId }),
            { headers: await this.defaultHeaders() }
        );

        if (response.ok) {
            return response.text();
        } else {
            return undefined;
        }
    }

    protected envelopeURL(
        path: string,
        params: { [index: string]: string } = {}
    ): string {
        const url = `${this.baseURL}${path}`;

        const queryParams = { ...params };
        if (!('sessionId' in queryParams)) {
            queryParams['sessionId'] = this.sessionID;
        }

        return `${url}?${new URLSearchParams(queryParams)}`;
    }

    private customizationPath(): string {
        switch (this.api) {
            case APIType.CONTOURING:
                return 'contouring';
            case APIType.DOSE:
                return 'dose';
            case APIType.DATA:
                return 'data';
            case APIType.REGISTRATION:
                return 'registration';
            case APIType.TRANSLATION:
                return 'translation';
            default:
                return 'contouring';
        }
    }
}
